import { ButtonGroup, IconButton, IconCheckmark, IconClose, Table } from '@zillow/constellation';
import { usePostTaskMutation } from 'API/order/OrderTaskAPI';
import { Formik } from 'formik';
import React from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { TaskSchema } from 'validation/order/TaskSchema';
import { EditDueDateField } from './EditDueDate';
import { EditRoleField } from './EditRole';
import { EditTeammateField } from './EditTeammate';
import { EditTaskDescriptionField } from './EditTaskDescription';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { combineDateAndTime, taskToEditFormData } from './formTools';
import { EditDueDateTimeField } from './EditDueDateTime';

interface IAddTaskRowProps {
  onComplete: () => void;
}

export const AddTaskRow: React.FC<IAddTaskRowProps> = ({ onComplete }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const [postTask] = usePostTaskMutation();

  return (
    <Formik
      validationSchema={TaskSchema}
      initialValues={taskToEditFormData({ completed: false })}
      onSubmit={async (values) => {
        const { description = '', role, teammate, dueDateDate, dueDateTime } = values;
        const body = {
          orderId,
          description,
          role: role ?? '',
          teammateId: teammate,
          dueDate: combineDateAndTime(dueDateDate, dueDateTime),
        };
        const result = await postTask({ orderId, body });
        checkForMutationSuccess(result, 'adding task', 'Error adding the task.');
        onComplete();
      }}
    >
      {({ submitForm }) => (
        <Table.Row key={'add-task-row'}>
          <Table.Cell></Table.Cell>
          <Table.Cell>
            <EditTaskDescriptionField />
          </Table.Cell>
          <Table.Cell>
            <EditRoleField />
          </Table.Cell>
          <Table.Cell>
            <EditTeammateField />
          </Table.Cell>
          <Table.Cell>
            <EditDueDateField /> at <EditDueDateTimeField />
          </Table.Cell>
          <Table.Cell>
            <ButtonGroup conjoined aria-label={'Add Task Save Controls'}>
              <IconButton
                type={'button'}
                buttonType={'primary'}
                icon={<IconCheckmark />}
                title={'Save Task'}
                size={'sm'}
                onClick={submitForm}
                data-test-id={'save-task-button'}
              />
              <IconButton
                type={'button'}
                buttonType={'caution'}
                icon={<IconClose />}
                title={'Cancel'}
                size={'sm'}
                onClick={onComplete}
              />
            </ButtonGroup>
          </Table.Cell>
        </Table.Row>
      )}
    </Formik>
  );
};
